import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { InputElementMetaData, MultiSelectDropdownComponent } from 'remarketing-ui-components-lib';
import { LaunchDarklyService, PurchasedItemDto, ReportType } from 'remarketing-angular-library';
import { take, takeUntil } from 'rxjs/operators';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { AnalyticsService, SaComponent } from 'smartauction-frontend-micro-library';
import { ReportUtilityService } from '../../services/report-utility.service';
import { ErrorModalComponent } from '@ally/remarketing/shared/ui';
import { EmailModalComponent } from '../email-modal/email-modal.component';
import { ReportHeaderType, ReportRole } from '../../enums';
import { NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';

export interface ReportDropdownList {
  name: string,
  defaultChecked: boolean,
  show: boolean,
  type: ReportType
}

@Component({
    selector: 'app-report-header',
    templateUrl: './report-header.component.html',
    styleUrls: ['./report-header.component.scss'],
    imports: [
        TranslocoDirective,
        RouterLink,
        NgClass,
        MultiSelectDropdownComponent,
        EmailModalComponent,
        ErrorModalComponent
    ]
})
export class ReportHeaderComponent extends SaComponent implements OnInit {
  @Output() reportTypeChanged = new EventEmitter<ReportType>();
  @Output() reportDropdownChanged = new EventEmitter<ReportType[]>();
  @Input() selectedReportType: ReportType;
  @Input() scrollPosition: number;
  @Input() backLinkText: string;
  @Input() backRouterLink: string;
  @Input() headerType: ReportHeaderType;
  @Input() purchasedItemDto: PurchasedItemDto[];
  @Input() reportRole: ReportRole;
  @ViewChild(ErrorModalComponent) errorModal: ErrorModalComponent;
  @ViewChild(EmailModalComponent) emailModal: EmailModalComponent;

  reportTypes = ReportType;
  reportHeaderType = ReportHeaderType;
  selectedReportTypes: ReportType[] = [];
  reportDropdownOptions: InputElementMetaData[] = [];
  multipleBuyersSelected = false;

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly reportUtilityService: ReportUtilityService,
    private readonly analyticsService: AnalyticsService
  ) {
    super();
  }

  reportDropdownList: { [key: string]: ReportDropdownList } = {};

  reportDropdownTranslations = {
    textAuthorization: '',
    textRecordOfSale: '',
    textSellerInvoice: '',
    textMore: '',
    textSelect: ''
  };
  errorMessage = '';

  isSelectedReportType(reportType: ReportType): boolean {
    return this.selectedReportType === reportType;
  }

  ngOnInit() {
    this.getTranslations();
  }

  getTranslations() {
    this.translocoService
      .selectTranslateObject('reports.reportDropdown')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.reportDropdownTranslations = translate;
        this.setReportTypeDropdownOptions();
        this.updateSelectedReports(this.reportDropdownOptions);
      });
  }

  get isSellerRecordOfSaleDropdownEnabled() {
    return this.launchDarklyService.getFeatureFlag('auctionSummary-ros-sellerRecordOfSaleDropdownEnabled', false);
  }

  setReportTypeDropdownOptions() {
    this.reportDropdownOptions = [];
    this.reportDropdownList = {
      AUTHORIZATION: {
        name: this.reportDropdownTranslations.textAuthorization,
        show: true,
        defaultChecked: false,
        type: ReportType.AUTHORIZATION
      },
      RECORD_OF_SALE: {
        name: this.reportDropdownTranslations.textRecordOfSale,
        show: true,
        defaultChecked: !this.isSellerInvoiceVisible,
        type: ReportType.RECORD_OF_SALE
      },
      SELLER_INVOICE: {
        name: this.reportDropdownTranslations.textSellerInvoice,
        show: this.isSellerInvoiceVisible,
        defaultChecked: true,
        type: ReportType.SELLER_INVOICE
      }
    };

    for (const key in this.reportDropdownList) {
      if (this.reportDropdownList[key].show) {
        this.reportDropdownOptions.push({
          id: this.reportDropdownList[key].type,
          class: 'custom-control-input',
          groupName: 'report',
          name: this.reportDropdownList[key].name,
          value: this.reportDropdownList[key].name,
          dataTrackname: 'report',
          ariaLabel: 'report',
          isChecked: this.reportDropdownList[key].defaultChecked
        } as InputElementMetaData);
      }
    }
  }

  updateSelectedReports(data: InputElementMetaData[] = null) {
    let selectedReports;

    if (data) {
      selectedReports = data;
    }

    const selectedReportTypes = selectedReports.filter(report => {
      return report.isChecked;
    });

    this.selectedReportTypes = [];
    selectedReportTypes.forEach(report => {
      this.selectedReportTypes.push(ReportType[report.id]);
    });

    this.reportDropdownChanged.emit(this.selectedReportTypes);
  }

  analyticsOnSelectReport(data: InputElementMetaData = null) {
    this.analyticsService.trackEvent('checkbox', 'Selected ' + data.id + ' - ' + data.isChecked);
  }

  getPlaceholderText() {
    if (this.selectedReportTypes.length <= 0) {
      return this.reportDropdownTranslations.textSelect;
    } else {
      let placeholderText = this.reportDropdownList[this.selectedReportTypes[0]].name;
      if (this.selectedReportTypes.length > 1) {
        placeholderText += ` ( +${this.selectedReportTypes.length - 1} ${this.reportDropdownTranslations.textMore} )`;
      }

      return placeholderText;
    }
  }

  printPurchaseItems() {
    this.reportUtilityService.printPurchasedItems(this.purchasedItemDto, null, this.selectedReportTypes);
  }

  showErrorModal() {
    this.errorModal.message = this.errorMessage;
    this.errorModal.show();
  }

  showEmailModal() {
    this.checkMultipleBuyers();
    // restrict email to multiple buyers iff the reports are generated from the sell sold page.
    if (this.multipleBuyersSelected && this.reportRole === ReportRole.SELLER) {
      this.translocoService
        .selectTranslateObject('reports.reportHeader.emailReportErrorForDifferentBuyers')
        .pipe(take(1))
        .subscribe(value => {
          this.errorMessage = value;
        });
      this.showErrorModal();
    } else {
      this.emailModal.show();
    }
  }

  checkMultipleBuyers() {
    const selectedAuctionItemBuyerOrgEntities = this.purchasedItemDto.map(
      purchasedItemDto => purchasedItemDto.buyer.oeUuid
    );
    const buyerUuidSet = new Set(selectedAuctionItemBuyerOrgEntities);
    this.multipleBuyersSelected = buyerUuidSet.size > 1;
  }

  get isSellerInvoiceVisible() {
    return this.isSellerRecordOfSaleDropdownEnabled && this.reportRole === ReportRole.SELLER;
  }
}
